import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { PackagesContext } from "../context/PackagesContext";
import { ThemeContext } from "../context/ThemeContext";
import api from "../services/api";
import { motion } from "framer-motion";
import {
  StarIcon,
  MapPinIcon,
  ChevronRightIcon,
  HeartIcon,
} from "@heroicons/react/24/solid";
import { getPackageById } from "../services/packageService";
import PopularPackageLinks from "./PopularPackageLinks";
import PackagesCards from "./PackagesCards";

const AllPackages = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { packages: contextPackages, loading: contextLoading } =
    useContext(PackagesContext);
  const [searchParams] = useSearchParams();
  const destinationParam = searchParams.get("destination");

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    priceRange: [0, 60000],
    rating: null,
    villaType: "",
    amenities: [],
    searchQuery: "",
  });
  const navigate = useNavigate();

  const [favorites, setFavorites] = useState(new Set());
  const [activePackage, setActivePackage] = useState(null);
  const [selectedImages, setSelectedImages] = useState({});

  useEffect(() => {
    console.log("destinationParam:", destinationParam);

    const filtered = destinationParam
      ? contextPackages.filter(pkg => {
        const pkgCountry = pkg.country?.trim().toLowerCase();
        const param = destinationParam.trim().toLowerCase();

        console.log(`Checking package ID ${pkg.id} | country: "${pkgCountry}" vs param: "${param}"`);

        return pkgCountry === param;
      })
      : contextPackages;

    console.log("Filtered packages:", filtered.map(p => p.id)); // Shows matching IDs
    setPackages(filtered);
    setLoading(false);
  }, [destinationParam, contextPackages]);



  const transformedPackages = useMemo(() => {
    if (!packages || packages.length === 0) return [];
    try {
      return packages.map((pkg) => ({
        ...pkg,
        name: pkg.packageName,
        images: [pkg.image2].filter(Boolean),
        features: [pkg.destination, pkg.country],
        villaType: pkg.international ? "international" : "domestic",
        rating: 4.5,
        location: pkg.destination,
        id: pkg.id,
      }));
    } catch (err) {
      setError("Failed to process package data.");
      return [];
    }
  }, [packages]);

  const filteredPackages = transformedPackages.filter((pkg) => {
    const name = pkg.name || "";
    const matchesSearch = name
      .toLowerCase()
      .includes(filters.searchQuery.toLowerCase());
    const matchesPrice =
      pkg.price >= filters.priceRange[0] && pkg.price <= filters.priceRange[1];
    const matchesRating = !filters.rating || pkg.rating >= filters.rating;
    const matchesVillaType =
      !filters.villaType || pkg.villaType === filters.villaType;
    const id = !filters.id || pkg.id === filters.id;
    return (
      matchesSearch && matchesPrice && matchesRating && matchesVillaType && id
    );
  });

  const toggleFavorite = (id) => {
    const newFavorites = new Set(favorites);
    newFavorites.has(id) ? newFavorites.delete(id) : newFavorites.add(id);
    setFavorites(newFavorites);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 pt-32">
      <div className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search packages..."
              className="w-full px-6 py-3 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              value={filters.searchQuery}
              onChange={(e) =>
                setFilters({ ...filters, searchQuery: e.target.value })
              }
            />
            <svg
              className="w-5 h-5 absolute right-4 top-4 text-gray-400 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 sticky top-20 h-fit">
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase mb-3">
                Villa Type
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { type: "international", label: "International", icon: "🌍" },
                  { type: "domestic", label: "Domestic", icon: "🏡" },
                ].map((villa) => (
                  <button
                    key={villa.type}
                    onClick={() =>
                      setFilters({ ...filters, villaType: villa.type })
                    }
                    className={`p-3 rounded-lg border transition-all flex flex-col items-center ${filters.villaType === villa.type
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900"
                      : "border-gray-200 dark:border-gray-600 hover:border-blue-300"
                      }`}
                  >
                    <span className="text-2xl mb-2">{villa.icon}</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{villa.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <main className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-20 text-gray-600 dark:text-gray-300">
                Loading packages...
              </div>
            ) : error ? (
              <div className="text-center py-20 text-red-500">
                Error: {error}
              </div>
            ) : filteredPackages.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-4xl mb-4">🌴</div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  No packages found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Try adjusting your filters or search terms
                </p>
              </div>
            ) : (
              <div className="grid gap-6">
                {filteredPackages.map((pkg) => (
                  <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 dark:border-gray-700 relative"
                  >
                    <button
                      onClick={() => toggleFavorite(pkg.id)}
                      className="absolute top-4 right-4 p-2 rounded-full bg-white dark:bg-gray-700 shadow-sm hover:bg-red-50 transition-colors"
                    >
                      <HeartIcon
                        className={`w-6 h-6 ${favorites.has(pkg.id)
                          ? "text-red-500 fill-current"
                          : "text-gray-400 dark:text-gray-300"
                          }`}
                      />
                    </button>

                    <div className="flex flex-col md:flex-row p-6">
                      <div className="w-full md:w-64 h-64 relative overflow-hidden rounded-xl mb-4 md:mb-0">
                        <img
                          src={selectedImages[pkg.id] || pkg.images?.[0]}
                          onClick={() =>
                            navigate(
                              `/packages-description?destination=${pkg.id}`
                            )
                          }
                          alt={pkg.name}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105 cursor-pointer"
                        />
                      </div>

                      <div className="md:ml-6 flex-1 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                              {pkg.name}
                            </h3>
                            <span className="flex items-center text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                              <StarIcon className="w-4 h-4 mr-1" />
                              {pkg.rating}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-4">
                            <MapPinIcon className="w-5 h-5 mr-2 text-gray-400" />
                            {pkg.location}
                          </div>
                          {pkg.features?.map((feature, index) => (
                            <span
                              key={`${feature}-${index}`}
                              className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                              {feature}
                            </span>
                          ))}

                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm text-gray-500 dark:text-gray-300">
                              Starting from
                            </span>
                            <p className="text-lg font-bold">
                              <span className="text-[#007bff]">
                                ₹{pkg.price.toLocaleString()}
                              </span>
                            </p>
                          </div>
                          <button
                            className="bg-[#007bff] text-white font-semibold px-6 py-3 rounded-lg hover:bg-gradient-to-r hover:from-[#66bfff] hover:to-[#3399ff] transition-all flex items-center shadow-lg shadow-blue-300/40 hover:shadow-xl hover:shadow-blue-400/50"
                            onClick={() =>
                              navigate(`/packages-description?destination=${pkg.id}`)
                            }
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {activePackage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-3xl p-6 relative text-gray-900 dark:text-white"
          >
            <button
              onClick={() => setActivePackage(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              ✕
            </button>
            <h2 className="text-2xl font-bold mb-4">{activePackage.name}</h2>
            <p className="mb-2 text-gray-600 dark:text-gray-300">{activePackage.description}</p>
            <p className="mb-2 text-gray-600 dark:text-gray-300">
              Destination: {activePackage.destination}
            </p>
            <p className="mb-2 text-gray-600 dark:text-gray-300">
              Nights: {activePackage.numberOfNights}, Days: {activePackage.numberOfDays}
            </p>
            <p className="mb-2 text-gray-600 dark:text-gray-300">
              Valid Until: {activePackage.validUntil}
            </p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {activePackage.images?.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Package ${idx}`}
                  className="w-full h-48 object-cover rounded-lg"
                />
              ))}
            </div>
          </motion.div>
        </div>
      )}
      <PackagesCards />
      <PopularPackageLinks />
    </div>
  );
};

export default AllPackages;
